import {
  ArrowClockwise,
  CheckCircle,
  Info,
  WarningCircle,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useI18n } from "../i18n";
import { displayLabel } from "../localization";

export type Tone = "neutral" | "purple" | "green" | "amber" | "red" | "blue";

export function toneForStatus(status: string): Tone {
  if (
    /ACTIVE|READY|CONNECTED|COMPLETED|CLEARED|AUTHORITATIVE|OK/u.test(status)
  ) {
    return "green";
  }
  if (/ERROR|FAILED|REJECTED|CRITICAL|RESTRICTED/u.test(status)) return "red";
  if (/PENDING|WAITING|ATTENTION|QUARANTINED|UNKNOWN/u.test(status))
    return "amber";
  if (/SYNC|PROCESS|RUNNING|CONNECTING/u.test(status)) return "blue";
  if (/DRAFT|PAUSED|ARCHIVED|CANCELLED/u.test(status)) return "neutral";
  return "purple";
}

export function StatusBadge({
  status,
  label,
}: {
  status: string;
  label?: string;
}) {
  const { locale } = useI18n();
  return (
    <span className={`status-badge status-badge--${toneForStatus(status)}`}>
      <span aria-hidden="true" className="status-badge__dot" />
      {label ?? displayLabel(status, locale)}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
}: {
  title: string;
  description: string;
  eyebrow?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="page-header__eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function PageState({
  kind,
  title,
  message,
  onRetry,
}: {
  kind: "loading" | "empty" | "error";
  title: string;
  message?: string;
  onRetry?: () => void;
}) {
  const { locale } = useI18n();
  const Icon =
    kind === "error" ? WarningCircle : kind === "empty" ? Info : ArrowClockwise;
  return (
    <div
      aria-live="polite"
      className={`page-state page-state--${kind}`}
      role="status"
    >
      <Icon
        aria-hidden="true"
        size={28}
        weight={kind === "loading" ? "regular" : "duotone"}
      />
      <strong>{title}</strong>
      {message ? <p>{message}</p> : null}
      {onRetry ? (
        <button
          className="button button--secondary"
          onClick={onRetry}
          type="button"
        >
          <ArrowClockwise aria-hidden="true" size={16} />
          {locale === "zh" ? "重新尝试" : "Try again"}
        </button>
      ) : null}
    </div>
  );
}

export function TrustScore({ value }: { value: number }) {
  const tone = value >= 85 ? "green" : value >= 65 ? "amber" : "red";
  return (
    <span className={`trust-score trust-score--${tone}`}>
      <CheckCircle aria-hidden="true" size={16} weight="fill" />
      <strong>{Math.round(value)}</strong>
      <span>/100</span>
    </span>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  icon,
  tone = "purple",
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: ReactNode;
  tone?: Tone;
}) {
  return (
    <article className="metric-card">
      <span className={`metric-card__icon metric-card__icon--${tone}`}>
        {icon}
      </span>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}
